import { useState } from "react";
import { Alert, AlertTitle, Box, Button, Collapse, Tooltip, Typography } from "@mui/material";
import {
  AccessTime,
  Block,
  Check,
  DoneAll,
  ErrorOutline,
  FilterAlt,
  GppMaybe,
  HelpOutline,
  WatchLater,
} from "@mui/icons-material";

import {
  ACK_LIDA,
  ACK_RECEBIDA_NO_APARELHO,
  ACK_RECEBIDA_NO_SERVIDOR,
  STATUS_ADIADA_ANTIBAN,
  STATUS_BLOQUEADA_SMARTFILTER,
  STATUS_CONTATO_BLOQUEADO,
  STATUS_ENTREGUE_APESAR_DO_ERRO,
  STATUS_ERRO,
  STATUS_FILA,
  STATUS_SUCESSO,
  STATUS_SUPRIMIDA,
  resolveStatusVisual,
} from "../../util/messageStatus";
import { traduzirErro } from "../../util/messageErrors";

// Apresentação dos estados de uma mensagem no relatório.
//
// A tabela e o card sempre consultaram o status por conta própria, cada um com
// a sua lista de casos — e foi assim que a tabela ficou sem tratar estados que o
// card já conhecia, deixando a célula de status em branco. Aqui os dois passam a
// beber da mesma fonte: `resolveStatusVisual` decide, este arquivo desenha.

/** Ícone que representa o estágio de entrega confirmado pelo WhatsApp. */
function IconeDeEntrega({ ack }) {
  if (ack >= ACK_LIDA) return <DoneAll color="primary" />;
  if (ack >= ACK_RECEBIDA_NO_APARELHO) return <DoneAll color="disabled" />;
  if (ack >= ACK_RECEBIDA_NO_SERVIDOR) return <Check color="success" />;

  // Enviada, mas o WhatsApp ainda não confirmou nada. Antes essa situação
  // mostrava o mesmo check verde de "enviado com sucesso" — o cliente lia como
  // entrega confirmada, que é justamente o que ainda não aconteceu.
  return <Check color="disabled" />;
}

const ICONES = {
  [STATUS_FILA]: <AccessTime color="info" />,
  [STATUS_ADIADA_ANTIBAN]: <WatchLater color="warning" />,
  [STATUS_SUPRIMIDA]: <GppMaybe color="disabled" />,
  [STATUS_BLOQUEADA_SMARTFILTER]: <FilterAlt color="disabled" />,
  [STATUS_CONTATO_BLOQUEADO]: <Block color="error" />,
  [STATUS_ERRO]: <ErrorOutline color="error" />,
};

/**
 * Ícone de status de uma linha da tabela, com o motivo no tooltip.
 *
 * Nunca devolve vazio: estado desconhecido vira um ícone de interrogação em vez
 * de célula em branco, que é o que acontecia com status novos.
 */
export function MessageStatusIcon({ message }) {
  const visual = resolveStatusVisual(message);

  if (
    visual.chave === STATUS_SUCESSO ||
    visual.chave === STATUS_ENTREGUE_APESAR_DO_ERRO
  ) {
    return (
      <Tooltip title={visual.rotulo}>
        <span style={{ display: "inline-flex" }}>
          <IconeDeEntrega ack={visual.ack} />
        </span>
      </Tooltip>
    );
  }

  const icone = ICONES[visual.chave] || <HelpOutline color="disabled" />;

  return (
    <Tooltip title={visual.rotulo}>
      <span style={{ display: "inline-flex" }}>{icone}</span>
    </Tooltip>
  );
}

/**
 * Bloco com o motivo da falha já traduzido.
 *
 * O texto técnico original fica recolhido: o cliente lê a explicação em
 * português, e o suporte abre o detalhe quando precisa da string crua.
 */
export function MessageErrorDetails({ erro }) {
  const [mostrarTecnico, setMostrarTecnico] = useState(false);
  const traduzido = traduzirErro(erro);

  if (!traduzido) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Não foi possível identificar o motivo da falha.
      </Alert>
    );
  }

  // Quando a tradução não reconheceu o motivo, o texto cru é a informação mais
  // útil que existe — então ele aparece aberto, sem esconder atrás do botão.
  const tecnicoRelevante = traduzido.tecnico && !traduzido.generica;

  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <AlertTitle sx={{ fontWeight: "bold" }}>{traduzido.titulo}</AlertTitle>

      <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
        {traduzido.explicacao}
      </Typography>

      {traduzido.acao && (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "normal" }}>
          <strong>O que fazer:</strong> {traduzido.acao}
        </Typography>
      )}

      {traduzido.generica && traduzido.tecnico && (
        <Typography
          variant="caption"
          component="p"
          sx={{ mt: 1, opacity: 0.85, wordBreak: "break-word" }}
        >
          {traduzido.code ? `[${traduzido.code}] ` : ""}
          {traduzido.tecnico}
        </Typography>
      )}

      {tecnicoRelevante && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            color="inherit"
            sx={{ textTransform: "none", padding: 0, minWidth: 0, opacity: 0.8 }}
            onClick={() => setMostrarTecnico((atual) => !atual)}
          >
            {mostrarTecnico ? "ocultar detalhe técnico" : "ver detalhe técnico"}
          </Button>

          <Collapse in={mostrarTecnico} timeout="auto" unmountOnExit>
            <Typography
              variant="caption"
              component="p"
              sx={{ mt: 0.5, opacity: 0.85, wordBreak: "break-word" }}
            >
              {traduzido.code ? `[${traduzido.code}] ` : ""}
              {traduzido.tecnico}
            </Typography>
          </Collapse>
        </Box>
      )}
    </Alert>
  );
}

/**
 * Aviso que explica a situação da mensagem dentro do card.
 *
 * Silencioso de propósito quando não há o que explicar (envio normal): um aviso
 * a cada linha do relatório vira ruído e o cliente para de ler todos.
 */
export function MessageStatusNotice({ message }) {
  const visual = resolveStatusVisual(message);

  // Falha de envio de verdade: mostra o motivo traduzido. Os demais estados têm
  // uma explicação própria e não carregam `erro` no documento.
  if (visual.chave === STATUS_ERRO) {
    return <MessageErrorDetails erro={message.erro} />;
  }

  if (!visual.descricao) return null;

  const motivoAnterior =
    visual.chave === STATUS_ENTREGUE_APESAR_DO_ERRO ? traduzirErro(message.erro) : null;

  return (
    <Alert severity={visual.severidade} sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
        {visual.descricao}
      </Typography>

      {motivoAnterior && (
        <Typography variant="caption" component="p" sx={{ mt: 0.5, opacity: 0.85 }}>
          Motivo registrado na tentativa que falhou: {motivoAnterior.titulo}.
        </Typography>
      )}
    </Alert>
  );
}

/** Linha do tempo da entrega, do envio até a leitura. */
export function MessageDeliveryTimeline({ message }) {
  const visual = resolveStatusVisual(message);

  const etapas = [
    {
      rotulo: "Recebida no servidor",
      alcancada: visual.ack >= ACK_RECEBIDA_NO_SERVIDOR,
      quando: message.sendAt,
    },
    {
      rotulo: "Recebida no aparelho",
      alcancada: visual.ack >= ACK_RECEBIDA_NO_APARELHO,
      quando: message.receivedAt,
    },
    {
      rotulo: "Lida",
      alcancada: visual.ack >= ACK_LIDA,
      quando: message.readAt,
    },
  ];

  const formatoData = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        mb: 2,
        justifyContent: "space-around",
      }}
    >
      {etapas.map((etapa) => (
        <Box key={etapa.rotulo} sx={{ textAlign: "center", minWidth: 120 }}>
          {etapa.alcancada ? (
            <DoneAll color="primary" fontSize="small" />
          ) : (
            <AccessTime color="disabled" fontSize="small" />
          )}

          <Typography
            variant="caption"
            component="p"
            sx={{ fontWeight: "bold", opacity: etapa.alcancada ? 1 : 0.5 }}
          >
            {etapa.rotulo}
          </Typography>

          {etapa.alcancada && etapa.quando && (
            <Typography variant="caption" component="p" sx={{ opacity: 0.7 }}>
              {new Date(etapa.quando).toLocaleDateString("pt-br", formatoData)}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}
